import { useState } from "react";
import { Loader2, Tag, Check, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { getPaddleEnvironment } from "@/lib/paddle";

export interface AppliedDiscount {
  code: string;
  type: "percentage" | "flat" | "flat_per_seat";
  amount: string;
  currency?: string;
  description?: string;
}

export function DiscountCodeInput({ onApplied }: { onApplied: (d: AppliedDiscount | null) => void }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState<AppliedDiscount | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const apply = async () => {
    if (!code.trim()) return;
    setBusy(true);
    setError(null);
    const { data } = await supabase.functions.invoke("validate-discount-code", {
      body: { code: code.trim(), environment: getPaddleEnvironment() },
    });
    setBusy(false);
    if (!data?.valid) {
      setError("Code not found or expired");
      onApplied(null);
      return;
    }
    const d: AppliedDiscount = {
      code: data.code,
      type: data.type,
      amount: data.amount,
      currency: data.currency,
      description: data.description,
    };
    setApplied(d);
    onApplied(d);
  };

  const clear = () => {
    setApplied(null);
    setCode("");
    setError(null);
    onApplied(null);
  };

  if (applied) {
    const label = applied.type === "percentage"
      ? `${applied.amount}% off`
      : `${(Number(applied.amount) / 100).toFixed(2)} ${applied.currency ?? ""} off`;
    return (
      <div className="flex items-center justify-center gap-2 text-sm">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-primary">
          <Check className="h-3.5 w-3.5" />
          <span className="font-medium">{applied.code}</span>
          <span className="text-xs">· {label}</span>
        </span>
        <button onClick={clear} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <X className="h-3 w-3" /> Remove
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5"
      >
        <Tag className="h-3 w-3" /> Have a discount code?
      </button>
    );
  }

  return (
    <div className="flex items-center justify-center gap-2 max-w-sm mx-auto">
      <Input
        placeholder="Enter code"
        value={code}
        onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(null); }}
        onKeyDown={(e) => { if (e.key === "Enter") apply(); }}
        className="h-9 uppercase"
        maxLength={32}
      />
      <Button size="sm" variant="outline" onClick={apply} disabled={busy || !code.trim()}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
      </Button>
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}